import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, MessageSquareText, CalendarClock, XCircle } from "lucide-react";
import { Navbar, Footer } from "../components/SiteChrome";
import { fetchBookingStatus, formatUSD } from "../lib/api";

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

const smsStatusLabel = (booking) => {
  if (booking.sms_status === "sent") return `Sent to ${booking.client.phone}`;
  if (booking.sms_status === "mocked") return "Logged (SMS not configured in this environment)";
  return "Will be sent shortly";
};

const BookingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed | error
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await fetchBookingStatus(sessionId);
        if (cancelled) return;
        setBooking(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["expired", "failed", "cancelled"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
          setError("Payment status check timed out. Please check your email.");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e?.response?.data?.detail || e.message);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div>
      <Navbar />
      <section className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32">
        {status === "polling" && (
          <div data-testid="success-polling" className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#C9A961] mx-auto mb-6" />
            <div className="font-display text-3xl font-semibold">
              Confirming your booking…
            </div>
            <div className="text-sm text-[#5c5a56] mt-3">
              We&rsquo;re verifying your payment with Stripe.
            </div>
          </div>
        )}

        {status === "paid" && booking && (
          <div data-testid="success-paid">
            <div className="flex items-center gap-3 mb-8">
              <CheckCircle2 className="w-7 h-7 text-[#C9A961]" />
              <div className="overline">Booked & paid</div>
            </div>
            <h1 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-none">
              You&rsquo;re on the calendar.
            </h1>
            <p className="mt-8 text-lg text-[#5c5a56] max-w-2xl leading-relaxed">
              Thanks, {booking.client.name}. Your {booking.offering_name} (
              {booking.tier_name}) is confirmed.
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Summary
                icon={CalendarClock}
                label="Kickoff"
                value={`${booking.slot_date} · ${booking.slot_time} PT`}
              />
              <Summary
                icon={MessageSquareText}
                label="SMS confirmation"
                value={smsStatusLabel(booking)}
              />
              <Summary label="Amount paid" value={formatUSD(booking.amount)} />
              <Summary label="Billing" value={booking.tier_billing} />
            </div>

            <div className="mt-12 bg-[#0F1A36] text-[#f5f1e8] p-8 md:p-12">
              <div className="overline !text-[#C9A961] mb-4">What happens next</div>
              <ol className="space-y-3 text-sm md:text-base text-[#f5f1e8]/80 list-decimal pl-5">
                <li>
                  We&rsquo;ll email you a calendar invite for{" "}
                  <strong className="text-[#f5f1e8]">{booking.slot_date} · {booking.slot_time} PT</strong>.
                </li>
                <li>You&rsquo;ll receive an intake form 24h before kickoff.</li>
                <li>
                  On kickoff day, we map the smallest thing that creates the
                  biggest impact and lock the scope.
                </li>
              </ol>
            </div>

            <Link
              to="/"
              data-testid="back-to-home"
              className="btn-outline mt-12 inline-flex"
            >
              Back to home
            </Link>
          </div>
        )}

        {(status === "expired" || status === "failed" || status === "cancelled") && (
          <div data-testid="success-failed" className="text-center">
            <XCircle className="w-10 h-10 text-red-700 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-semibold">
              Payment didn&rsquo;t complete.
            </h1>
            <div className="text-sm text-[#5c5a56] mt-3">
              Status: {status}. You can retry from the service page.
            </div>
            <Link to="/" data-testid="failed-back-home" className="btn-primary mt-8 inline-flex">
              Back to home
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="success-error" className="text-center">
            <div className="font-display text-2xl font-semibold">
              We couldn&rsquo;t verify your booking.
            </div>
            <div className="text-sm text-[#5c5a56] mt-3">{error}</div>
            <Link to="/" data-testid="error-back-home" className="btn-outline mt-8 inline-flex">
              Back to home
            </Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

const Summary = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#d4cdb8] p-6">
    <div className="flex items-center gap-2 overline mb-3">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null} {label}
    </div>
    <div className="font-display text-lg font-medium text-[#0F1A36]">{value}</div>
  </div>
);

export default BookingSuccess;
